import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/permissions";
import { FolderOpen, TrendingUp, BarChart3, AlertTriangle, ChevronRight, CalendarClock, Users, Target, Wallet, Receipt, ShoppingCart, Building2 } from "lucide-react";
import { formatINR } from "@/lib/utils";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated counter
function CountUp({ target, prefix = "", suffix = "", duration = 1200 }) {
  const numTarget = typeof target === "string"
    ? parseFloat(target.replace(/[^0-9.]/g, "")) || 0
    : (target || 0);
  const isIntTarget = Number.isInteger(numTarget);
  const [value, setValue] = useState(numTarget);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    startRef.current = null;
    const step = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const raw = ease * numTarget;
      setValue(isIntTarget ? Math.round(raw) : Math.round(raw * 10) / 10);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setValue(numTarget); // always land exactly on target
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafRef.current);
      setValue(numTarget); // never leave a partial value on unmount
    };
  }, [numTarget, isIntTarget, duration]);

  if (typeof target === "string" && (target.includes("Cr") || target.includes("L") || target.includes("₹"))) {
    return <span>{target}</span>;
  }
  return <span>{prefix}{isIntTarget ? Math.round(value) : value.toFixed(1)}{suffix}</span>;
}

const CARD_CONFIGS = [
  {
    key: "total",
    label: "Total Projects",
    icon: FolderOpen,
    glow: "rgba(47,125,82,0.15)",
    border: "rgba(47,125,82,0.18)",
    bg: "rgba(47,125,82,0.06)",
    iconBg: "rgba(47,125,82,0.12)",
    iconColor: "#2F7D52",
    accentTop: true,
  },
  {
    key: "active",
    label: "Active Projects",
    icon: TrendingUp,
    glow: "rgba(22,163,74,0.15)",
    border: "rgba(22,163,74,0.18)",
    bg: "rgba(22,163,74,0.06)",
    iconBg: "rgba(22,163,74,0.12)",
    iconColor: "#16A34A",
    accentTop: true,
  },
  {
    key: "totalValue",
    label: "Total Portfolio",
    icon: BarChart3,
    glow: "rgba(245,158,11,0.15)",
    border: "rgba(245,158,11,0.2)",
    bg: "rgba(245,158,11,0.06)",
    iconBg: "rgba(245,158,11,0.12)",
    iconColor: "#D97706",
    isValue: true,
    accentTop: true,
  },
  {
    key: "delayed",
    label: "Delayed Projects",
    icon: AlertTriangle,
    glow: "rgba(220,38,38,0.12)",
    border: "rgba(220,38,38,0.18)",
    bg: "rgba(220,38,38,0.06)",
    iconBg: "rgba(220,38,38,0.12)",
    iconColor: "#DC2626",
    accentTop: true,
  },
];

const RAGBadge = ({ status }) => {
  const map = {
    green: { bg: "rgba(16,185,129,0.12)", color: "#34D399", label: "On Track" },
    amber: { bg: "rgba(245,158,11,0.12)", color: "#FBBF24", label: "At Risk" },
    red:   { bg: "rgba(239,68,68,0.12)",  color: "#F87171", label: "Delayed" },
  };
  const s = map[status] || map.green;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: "10px", fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: "100px", whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    active:    { bg: "rgba(47,125,82,0.1)",   color: "#2F7D52" },
    completed: { bg: "rgba(22,163,74,0.1)",   color: "#16A34A" },
    on_hold:   { bg: "rgba(217,119,6,0.1)",   color: "#D97706" },
    cancelled: { bg: "rgba(220,38,38,0.1)",   color: "#DC2626" },
  };
  const s = map[status] || map.active;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: "10px", fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: "100px" }}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

function SkeletonCard() {
  return (
    <div className="skeleton" style={{ height: "108px", borderRadius: "16px" }} />
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#FFFFFF", border: "1px solid rgba(46,100,68,0.12)", borderRadius: "10px", padding: "10px 14px", boxShadow: "0 4px 20px rgba(46,100,68,0.1)" }}>
      <p style={{ color: "#8AA496", fontSize: "11px", marginBottom: "4px" }}>{label}</p>
      <p style={{ color: "#1B2B22", fontSize: "15px", fontWeight: "700", fontFamily: "Outfit" }}>{payload[0].value}</p>
    </div>
  );
};

function FollowUpsWidget() {
  const [data, setData] = useState({ due_today: [], overdue: [] });
  useEffect(() => { axios.get(`${API}/crm/my-followups`).then(r => setData(r.data)).catch(() => {}); }, []);
  const total = (data.due_today?.length || 0) + (data.overdue?.length || 0);
  if (!total) return null;
  const Row = ({ l, overdue }) => (
    <Link to="/app/crm" data-testid={`followup-item-${l.id}`} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#F5F8F5] transition-colors">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[#0A1128] truncate">{l.contact_name || l.company_name || "Lead"}</div>
        <div className="text-xs text-[#64748B] truncate">{l.phone || "—"} · {(l.stage || "").replace(/_/g, " ")}</div>
      </div>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ml-2 ${overdue ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#FEF3C7] text-[#92400E]"}`}>{overdue ? "Overdue" : "Today"}</span>
    </Link>
  );
  return (
    <div data-testid="followups-widget" className="bg-white border border-[rgba(46,100,68,0.12)] rounded-xl p-5" style={{ boxShadow: "0 4px 20px rgba(46,100,68,0.06)" }}>
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock size={16} className="text-[#FFB703]" />
        <h3 className="font-heading font-semibold text-base text-[#0A1128]">My Follow-ups Today</h3>
        <span className="ml-auto text-xs font-bold text-[#2F7D52]">{total} to call</span>
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {(data.overdue || []).map(l => <Row key={l.id} l={l} overdue />)}
        {(data.due_today || []).map(l => <Row key={l.id} l={l} />)}
      </div>
    </div>
  );
}


/* ── Shared building blocks ─────────────────────────────────────────────── */

const cardStyle = { background: "#FFFFFF", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "16px", boxShadow: "0 2px 12px rgba(46,100,68,0.06)" };
const thStyle = { padding: "10px 16px", textAlign: "left", fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", whiteSpace: "nowrap" };

function StatCard({ label, value, icon: Icon, color = "#2F7D52", sub, i = 0, testid }) {
  return (
    <div className="animate-fade-up" data-testid={testid}
      style={{ ...cardStyle, padding: "20px", borderTop: "2px solid #FFB703", animationDelay: `${i * 0.07}s`, transition: "transform 0.25s ease, box-shadow 0.25s ease" }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; }} onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}>
      <div className="flex items-center justify-between mb-3">
        <span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496" }}>{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}1F` }}><Icon size={15} style={{ color }} /></div>
      </div>
      <div className="font-heading font-bold" style={{ fontSize: "26px", letterSpacing: "-0.03em", color: "#1B2B22" }}>{value ?? "—"}</div>
      {sub && <div style={{ color: "#8AA496", fontSize: "11px", marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, to, toLabel = "View All", children, className = "", testid }) {
  return (
    <div className={className} style={{ ...cardStyle, overflow: "hidden" }} data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid rgba(46,100,68,0.08)" }}>
        <h3 className="font-heading font-semibold text-sm" style={{ letterSpacing: "-0.02em", color: "#1B2B22" }}>{title}</h3>
        {to && <Link to={to} className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#2F7D52", textDecoration: "none" }}>{toLabel} <ChevronRight size={12} /></Link>}
      </div>
      {children}
    </div>
  );
}

const Empty = ({ children, colSpan = 5 }) => <tr><td colSpan={colSpan} style={{ padding: "40px 16px", textAlign: "center", color: "#8AA496", fontSize: "13px" }}>{children}</td></tr>;
const rowHover = { onMouseEnter: e => { e.currentTarget.style.background = "#F5FAF7"; }, onMouseLeave: e => { e.currentTarget.style.background = "transparent"; } };

/* ── Projects (admin, PM, procurement, field engineer) ──────────────────── */

function ProjectsSection({ user, title = "All Projects" }) {
  const [projects, setProjects] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API}/projects`);
        setProjects(data);
        const total = data.length;
        const active = data.filter(p => p.status === "active" && !p.is_archived).length;
        const completed = data.filter(p => p.status === "completed").length;
        const delayed = data.filter(p => p.rag_status === "red").length;
        const totalValue = data.reduce((a, p) => a + (p.contract_value || 0), 0);
        const fmtValue = totalValue >= 10000000 ? `₹${(totalValue / 10000000).toFixed(1)}Cr` : totalValue >= 100000 ? `₹${(totalValue / 100000).toFixed(1)}L` : `₹${totalValue.toLocaleString("en-IN")}`;
        setAnalytics({ total, active, completed, delayed, totalValue: fmtValue });
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, []);

  const chartData = [
    { name: "Active", value: analytics?.active || 0, color: "#2F7D52" },
    { name: "Completed", value: analytics?.completed || 0, color: "#16A34A" },
    { name: "Delayed", value: analytics?.delayed || 0, color: "#DC2626" },
  ];

  return (
    <div className="space-y-6" data-testid="dash-projects">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loading
          ? Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />)
          : CARD_CONFIGS.filter(card => card.key !== "totalValue" || can(user, "projects.value.view")).map((card, i) => {
              const val = analytics?.[card.key];
              return <StatCard key={card.key} i={i} label={card.label} icon={card.icon} color={card.iconColor} value={val !== undefined ? <CountUp target={val} /> : "—"} />;
            })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div style={{ ...cardStyle, padding: "20px" }}>
          <h3 className="font-heading font-semibold text-sm mb-5" style={{ letterSpacing: "-0.02em", color: "#1B2B22" }}>Project Status</h3>
          {loading ? <div className="skeleton" style={{ height: "180px", borderRadius: "8px" }} /> : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} barSize={36} barCategoryGap="35%">
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8AA496", fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(46,100,68,0.04)" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{chartData.map((entry, i) => <Cell key={i} fill={entry.color} fillOpacity={0.85} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <Panel title={title} to="/app/projects" className="lg:col-span-2">
          {loading ? (
            <div className="p-5 space-y-3">{Array(4).fill(0).map((_, i) => <div key={i} className="skeleton" style={{ height: "40px", borderRadius: "8px", animationDelay: `${i * 0.1}s` }} />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead><tr style={{ borderBottom: "1px solid rgba(46,100,68,0.07)" }}>
                  {["Project", "Client", "Progress", "Status", "RAG"].map((h, i) => <th key={h} style={thStyle} className={i === 1 ? "hidden md:table-cell" : i === 3 ? "hidden lg:table-cell" : ""}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {projects.slice(0, 8).map(p => (
                    <tr key={p.id} style={{ borderBottom: "1px solid rgba(46,100,68,0.05)", transition: "background 0.12s ease" }} {...rowHover}>
                      <td style={{ padding: "12px 16px" }}>
                        <Link to={`/app/projects/${p.id}`} style={{ color: "#1B2B22", fontWeight: "600", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "180px" }}>{p.name}</Link>
                        <div style={{ color: "#8AA496", fontSize: "11px", marginTop: "2px" }}>{p.capacity_kwp} kWp · {p.location}</div>
                      </td>
                      <td style={{ padding: "12px 16px", color: "#5A7264", fontSize: "12px" }} className="hidden md:table-cell">{p.client_name}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <div className="flex items-center gap-2">
                          <div style={{ width: "72px", height: "4px", background: "rgba(46,100,68,0.1)", borderRadius: "4px", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${p.completion_percentage || 0}%`, background: "linear-gradient(90deg, #2F7D52, #16A34A)", borderRadius: "4px", transition: "width 1s ease" }} />
                          </div>
                          <span style={{ fontSize: "11px", color: "#8AA496" }}>{p.completion_percentage || 0}%</span>
                        </div>
                      </td>
                      <td style={{ padding: "12px 16px" }} className="hidden lg:table-cell"><StatusBadge status={p.status} /></td>
                      <td style={{ padding: "12px 16px" }}><RAGBadge status={p.rag_status} /></td>
                    </tr>
                  ))}
                  {projects.length === 0 && <Empty>No projects yet. <Link to="/app/projects" style={{ color: "#2F7D52" }}>Create your first project</Link></Empty>}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

/* ── Leads (admin, sales) ────────────────────────────────────────────────── */

const STAGE_LABELS = { new_lead: "New", contacted: "Contacted", site_survey_scheduled: "Survey scheduled", site_survey_done: "Survey done", proposal_sent: "Proposal sent", negotiation: "Negotiation", won: "Won", lost: "Lost" };
const OPEN_STAGES = ["new_lead", "contacted", "site_survey_scheduled", "site_survey_done", "proposal_sent", "negotiation"];

function LeadsSection({ user, mineOnly = false }) {
  const [report, setReport] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [r, l] = await Promise.all([
          axios.get(`${API}/crm/reports`).catch(() => ({ data: null })),
          axios.get(`${API}/crm/leads`, { params: mineOnly ? { assigned_to: user.id } : {} }),
        ]);
        setReport(r.data);
        let list = (l.data || []).filter(x => OPEN_STAGES.includes(x.stage));
        if (mineOnly && list.length === 0) { const all = await axios.get(`${API}/crm/leads`); list = (all.data || []).filter(x => OPEN_STAGES.includes(x.stage)); }
        list.sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
        setLeads(list);
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, [user.id, mineOnly]);

  const openValue = leads.reduce((a, l) => a + (l.deal_value || 0), 0);
  const funnel = OPEN_STAGES.map(s => ({ name: STAGE_LABELS[s], value: leads.filter(l => l.stage === s).length, color: "#2F7D52" }));
  const showValue = can(user, "projects.value.view");
  return (
    <div className="space-y-6" data-testid="dash-leads">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />) : <>
          <StatCard i={0} label={mineOnly ? "My open leads" : "Open leads"} icon={Users} value={<CountUp target={leads.length} />} sub={report ? `${report.total_this_month} new this month` : null} />
          <StatCard i={1} label="Pipeline value" icon={Target} color="#D97706" value={showValue ? formatINR(openValue, { zero: "₹0" }) : "—"} sub={showValue && report ? `forecast ${formatINR(report.revenue_forecast, { zero: "₹0" })}` : null} />
          <StatCard i={2} label="Won" icon={TrendingUp} color="#16A34A" value={report ? <CountUp target={report.won_count || 0} /> : "—"} sub={report ? `${report.conversion_rate}% conversion` : null} />
          <StatCard i={3} label="Proposals out" icon={Receipt} color="#7C3AED" value={<CountUp target={leads.filter(l => ["proposal_sent", "negotiation"].includes(l.stage)).length} />} sub="in proposal / negotiation" />
        </>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div style={{ ...cardStyle, padding: "20px" }}>
          <h3 className="font-heading font-semibold text-sm mb-5" style={{ letterSpacing: "-0.02em", color: "#1B2B22" }}>Pipeline by stage</h3>
          {loading ? <div className="skeleton" style={{ height: "180px", borderRadius: "8px" }} /> : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={funnel} barSize={22} barCategoryGap="30%">
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#8AA496", fontFamily: "Manrope" }} axisLine={false} tickLine={false} interval={0} />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(46,100,68,0.04)" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{funnel.map((e, i) => <Cell key={i} fill={e.color} fillOpacity={0.5 + i * 0.09} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <Panel title={mineOnly ? "My leads" : "Recent leads"} to="/app/crm" toLabel="Open CRM" className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead><tr style={{ borderBottom: "1px solid rgba(46,100,68,0.07)" }}>{["Lead", "Stage", "Value", "Next follow-up", "Owner"].map((h, i) => <th key={h} style={thStyle} className={i >= 3 ? "hidden md:table-cell" : ""}>{h}</th>)}</tr></thead>
              <tbody>
                {loading && <Empty>Loading…</Empty>}
                {!loading && leads.slice(0, 8).map(l => {
                  const due = l.next_follow_up_date;
                  const overdue = due && due.slice(0, 10) < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={l.id} style={{ borderBottom: "1px solid rgba(46,100,68,0.05)" }} {...rowHover}>
                      <td style={{ padding: "12px 16px" }}>
                        <Link to="/app/crm" style={{ color: "#1B2B22", fontWeight: "600", textDecoration: "none" }}>{l.company_name || l.contact_name || "Lead"}</Link>
                        <div style={{ color: "#8AA496", fontSize: "11px", marginTop: "2px" }}>{[l.contact_name !== l.company_name ? l.contact_name : null, l.capacity_kw ? `${l.capacity_kw} kW` : null, l.city].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td style={{ padding: "12px 16px" }}><span style={{ background: "rgba(47,125,82,0.1)", color: "#2F7D52", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: "100px", whiteSpace: "nowrap" }}>{STAGE_LABELS[l.stage] || l.stage}</span></td>
                      <td style={{ padding: "12px 16px", color: "#1B2B22", fontSize: "12px", fontWeight: 600 }}>{showValue ? formatINR(l.deal_value) : "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: "12px", color: overdue ? "#B91C1C" : "#5A7264" }} className="hidden md:table-cell">{due ? new Date(due).toLocaleDateString("en-IN") : "—"}</td>
                      <td style={{ padding: "12px 16px", color: "#5A7264", fontSize: "12px" }} className="hidden md:table-cell">{l.assigned_to_name || "—"}</td>
                    </tr>
                  );
                })}
                {!loading && leads.length === 0 && <Empty>No open leads. <Link to="/app/crm" style={{ color: "#2F7D52" }}>Add one in the CRM</Link></Empty>}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ── Payables & receivables (accountant) ────────────────────────────────── */

function MoneySection() {
  const [cash, setCash] = useState(null);
  const [recv, setRecv] = useState(null);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [c, r, p] = await Promise.all([
          axios.get(`${API}/ledger/cash-position`).catch(() => ({ data: null })),
          axios.get(`${API}/ledger/receivables`).catch(() => ({ data: null })),
          axios.get(`${API}/financials/po`).catch(() => ({ data: [] })),
        ]);
        setCash(c.data); setRecv(r.data);
        setPos((p.data || []).filter(x => x.status !== "void" && (x.balance_due ?? (x.total - (x.paid_amount || 0))) > 0.5).sort((a, b) => (a.date || "").localeCompare(b.date || "")));
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, []);
  const t = cash?.totals || {};
  const overdue = recv ? (recv.buckets?.["31-60"]?.amount || 0) + (recv.buckets?.["61-90"]?.amount || 0) + (recv.buckets?.["90+"]?.amount || 0) : 0;
  const payable = pos.reduce((a, p) => a + (p.balance_due ?? (p.total - (p.paid_amount || 0))), 0);
  const ageDays = (d) => d ? Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 86400000)) : 0;
  return (
    <div className="space-y-6" data-testid="dash-money">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />) : <>
          <StatCard i={0} label="Receivable" icon={Receipt} color="#D97706" value={formatINR(recv?.total_outstanding ?? t.receivable, { zero: "₹0" })} sub={`${recv?.invoices?.length || 0} open invoice${recv?.invoices?.length === 1 ? "" : "s"} · ${formatINR(overdue, { zero: "₹0" })} past 30 days`} testid="dash-receivable" />
          <StatCard i={1} label="Payable to vendors" icon={Wallet} color="#DC2626" value={formatINR(payable, { zero: "₹0" })} sub={`${pos.length} unpaid PO${pos.length === 1 ? "" : "s"}`} testid="dash-payable" />
          <StatCard i={2} label="Received (all projects)" icon={TrendingUp} color="#16A34A" value={formatINR(t.received, { zero: "₹0" })} sub={`of ${formatINR(t.billed, { zero: "₹0" })} billed`} />
          <StatCard i={3} label="Net cash position" icon={BarChart3} color={t.net_position < 0 ? "#DC2626" : "#2F7D52"} value={formatINR(t.net_position, { zero: "₹0" })} sub={t.funded_by_epc ? `${formatINR(t.funded_by_epc)} funded by you` : "received − paid out"} />
        </>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Receivables — oldest first" to="/app/payments?tab=receivables" toLabel="Receivables" testid="dash-recv-table">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead><tr style={{ borderBottom: "1px solid rgba(46,100,68,0.07)" }}>{["Invoice", "Client", "Outstanding", "Age"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {loading && <Empty colSpan={4}>Loading…</Empty>}
              {!loading && (recv?.invoices || []).slice().sort((a, b) => b.age_days - a.age_days).slice(0, 8).map(r => (
                <tr key={r.pi_id} style={{ borderBottom: "1px solid rgba(46,100,68,0.05)" }} {...rowHover}>
                  <td style={{ padding: "10px 16px" }}><Link to={`/app/projects/${r.project_id}/financials/pi`} style={{ color: "#1B2B22", fontWeight: 600, textDecoration: "none", fontFamily: "monospace", fontSize: "12px" }}>{r.pi_number}</Link><div style={{ color: "#8AA496", fontSize: "11px" }}>{r.project_name}</div></td>
                  <td style={{ padding: "10px 16px", color: "#5A7264", fontSize: "12px" }}>{r.client_name || "—"}</td>
                  <td style={{ padding: "10px 16px", fontWeight: 600, color: r.age_days > 60 ? "#B91C1C" : r.age_days > 30 ? "#B45309" : "#1B2B22" }}>{formatINR(r.balance)}</td>
                  <td style={{ padding: "10px 16px", fontSize: "12px", color: "#5A7264" }}>{r.age_days} d</td>
                </tr>
              ))}
              {!loading && !(recv?.invoices || []).length && <Empty colSpan={4}>Nothing outstanding.</Empty>}
            </tbody>
          </table>
        </Panel>
        <Panel title="Payables — unpaid purchase orders" to="/app/payments?tab=vendor" toLabel="Vendor payments" testid="dash-pay-table">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead><tr style={{ borderBottom: "1px solid rgba(46,100,68,0.07)" }}>{["PO", "Vendor", "Balance", "Age"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {loading && <Empty colSpan={4}>Loading…</Empty>}
              {!loading && pos.slice(0, 8).map(p => (
                <tr key={p.id} style={{ borderBottom: "1px solid rgba(46,100,68,0.05)" }} {...rowHover}>
                  <td style={{ padding: "10px 16px" }}><Link to={`/app/projects/${p.project_id}/financials/po`} style={{ color: "#1B2B22", fontWeight: 600, textDecoration: "none", fontFamily: "monospace", fontSize: "12px" }}>{p.po_number}</Link><div style={{ color: "#8AA496", fontSize: "11px" }}>{p.payment_status === "partial" ? `part-paid ${formatINR(p.paid_amount)}` : p.status}</div></td>
                  <td style={{ padding: "10px 16px", color: "#5A7264", fontSize: "12px" }}>{p.vendor_name}</td>
                  <td style={{ padding: "10px 16px", fontWeight: 600, color: "#1B2B22" }}>{formatINR(p.balance_due ?? (p.total - (p.paid_amount || 0)))}</td>
                  <td style={{ padding: "10px 16px", fontSize: "12px", color: ageDays(p.date) > 45 ? "#B91C1C" : "#5A7264" }}>{ageDays(p.date)} d</td>
                </tr>
              ))}
              {!loading && pos.length === 0 && <Empty colSpan={4}>No unpaid POs.</Empty>}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}

/* ── Purchase orders (procurement) ──────────────────────────────────────── */

function POSection() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API}/financials/po`).then(r => setPos((r.data || []).filter(p => p.status !== "void"))).catch(() => {}).finally(() => setLoading(false)); }, []);
  const open = pos.filter(p => p.status !== "received");
  const committed = pos.reduce((a, p) => a + (p.total || 0), 0);
  const unpaid = pos.reduce((a, p) => a + Math.max(0, p.balance_due ?? (p.total - (p.paid_amount || 0))), 0);
  const byVendor = Object.entries(pos.reduce((m, p) => { m[p.vendor_name] = (m[p.vendor_name] || 0) + (p.total || 0); return m; }, {})).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const recent = pos.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 8);
  return (
    <div className="space-y-6" data-testid="dash-pos">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />) : <>
          <StatCard i={0} label="Open POs" icon={ShoppingCart} value={<CountUp target={open.length} />} sub={`${pos.length} total`} />
          <StatCard i={1} label="Committed" icon={BarChart3} color="#D97706" value={formatINR(committed, { zero: "₹0" })} sub="all non-void POs" />
          <StatCard i={2} label="Unpaid to vendors" icon={Wallet} color="#DC2626" value={formatINR(unpaid, { zero: "₹0" })} sub={`${pos.filter(p => (p.balance_due ?? 1) > 0.5).length} PO${pos.filter(p => (p.balance_due ?? 1) > 0.5).length === 1 ? "" : "s"} outstanding`} />
          <StatCard i={3} label="Vendors" icon={Building2} color="#7C3AED" value={<CountUp target={new Set(pos.map(p => p.vendor_name)).size} />} sub={<Link to="/app/masters" style={{ color: "#2F7D52" }}>vendor master</Link>} />
        </>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div style={{ ...cardStyle, padding: "20px" }}>
          <h3 className="font-heading font-semibold text-sm mb-4" style={{ letterSpacing: "-0.02em", color: "#1B2B22" }}>Top vendors by PO value</h3>
          {byVendor.length === 0 && <div style={{ color: "#8AA496", fontSize: "13px" }}>No POs yet.</div>}
          {byVendor.map(([v, amt]) => (
            <div key={v} className="flex items-center gap-2 py-1.5" style={{ borderBottom: "1px solid rgba(46,100,68,0.05)" }}>
              <div className="flex-1 min-w-0" style={{ fontSize: "12px", color: "#1B2B22", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</div>
              <div style={{ fontSize: "12px", color: "#5A7264" }}>{formatINR(amt)}</div>
            </div>
          ))}
        </div>
        <Panel title="Recent purchase orders" to="/app/payments?tab=vendor" toLabel="Vendor payments" className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead><tr style={{ borderBottom: "1px solid rgba(46,100,68,0.07)" }}>{["PO", "Vendor", "Total", "Status", "Payment"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {loading && <Empty>Loading…</Empty>}
                {!loading && recent.map(p => (
                  <tr key={p.id} style={{ borderBottom: "1px solid rgba(46,100,68,0.05)" }} {...rowHover}>
                    <td style={{ padding: "10px 16px" }}><Link to={`/app/projects/${p.project_id}/financials/po`} style={{ color: "#1B2B22", fontWeight: 600, textDecoration: "none", fontFamily: "monospace", fontSize: "12px" }}>{p.po_number}</Link><div style={{ color: "#8AA496", fontSize: "11px" }}>{p.date}</div></td>
                    <td style={{ padding: "10px 16px", color: "#5A7264", fontSize: "12px" }}>{p.vendor_name}</td>
                    <td style={{ padding: "10px 16px", fontWeight: 600, color: "#1B2B22" }}>{formatINR(p.total)}</td>
                    <td style={{ padding: "10px 16px" }}><StatusBadge status={p.status === "received" ? "completed" : p.status === "sent" ? "active" : "on_hold"} /></td>
                    <td style={{ padding: "10px 16px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: p.payment_status === "paid" ? "#16A34A" : p.payment_status === "partial" ? "#D97706" : "#DC2626" }}>{p.payment_status === "partial" ? "part-paid" : p.payment_status || "unpaid"}</td>
                  </tr>
                ))}
                {!loading && recent.length === 0 && <Empty>No purchase orders yet.</Empty>}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ── Role → sections ─────────────────────────────────────────────────────── */

const SectionTitle = ({ children }) => <h2 className="font-heading font-semibold" style={{ fontSize: "15px", color: "#1B2B22", letterSpacing: "-0.02em" }}>{children}</h2>;

export default function DashboardPage() {
  const { user, workspace } = useAuth();
  const role = user?.role;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";

  // What each role sees first thing. Sections are still capability-gated so a role change never leaks data.
  const layout = {
    super_admin: ["projects", "leads"], company_admin: ["projects", "leads"], management_view: ["projects", "leads"],
    sales_person: ["leads"], project_manager: ["projects"], accountant: ["money"],
    procurement: ["pos", "projects"], field_engineer: ["projects"],
  }[role] || ["projects"];
  const allowed = {
    projects: can(user, "projects.view"), leads: can(user, "crm.view"),
    money: can(user, "cash_position.view") && can(user, "pi.view") && can(user, "po.view"), pos: can(user, "po.view"),
  };
  const sections = layout.filter(k => allowed[k]);
  const subtitle = { sales_person: "Your pipeline at a glance", accountant: "Payables & receivables", procurement: "Purchase orders & projects", field_engineer: "Your projects", project_manager: "Project portfolio overview" }[role] || "Projects & pipeline overview";
  const cta = role === "sales_person" ? { to: "/app/crm", label: "Open CRM", icon: Users } : role === "accountant" ? { to: "/app/payments", label: "Payments", icon: Wallet } : { to: "/app/projects", label: "View Projects", icon: FolderOpen };

  return (
    <div className="p-6 space-y-6 animate-fade-up" style={{ background: "#F5F8F5", minHeight: "100%" }} data-testid={`dashboard-${role}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold" style={{ fontSize: "26px", letterSpacing: "-0.03em", color: "#1B2B22" }}>{greeting}, {user?.full_name?.split(" ")[0]}</h1>
          <p style={{ color: "#8AA496", fontSize: "14px", marginTop: "4px" }}>{workspace?.name} — {subtitle}</p>
        </div>
        <Link to={cta.to} data-testid="new-project-btn" className="sf-btn-primary inline-flex items-center gap-2" style={{ padding: "10px 20px", fontSize: "13px", fontWeight: "700", borderRadius: "100px", textDecoration: "none" }}>
          <cta.icon size={14} /> {cta.label}
        </Link>
      </div>

      {allowed.leads && <FollowUpsWidget />}

      {sections.map((k, i) => (
        <div key={k} className="space-y-4">
          {sections.length > 1 && <SectionTitle>{{ projects: "Projects", leads: "Leads & pipeline", money: "Payables & receivables", pos: "Purchase orders" }[k]}</SectionTitle>}
          {k === "projects" && <ProjectsSection user={user} title={role === "field_engineer" ? "My Projects" : "All Projects"} />}
          {k === "leads" && <LeadsSection user={user} mineOnly={role === "sales_person"} />}
          {k === "money" && <MoneySection />}
          {k === "pos" && <POSection />}
        </div>
      ))}
      {sections.length === 0 && <div style={{ color: "#8AA496" }}>Nothing to show for your role yet.</div>}
    </div>
  );
}
